import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  MOCK_USER, MOCK_BOOKINGS, MOCK_TRANSACTIONS, MOCK_ACHIEVEMENTS,
  MOCK_CHECK_IN_HISTORY, MOCK_TRAVEL_STAMPS, MOCK_SEASONAL_CHALLENGES,
  MOCK_BINGO, MOCK_LEADERBOARD, MOCK_SPIN_PRIZES, MOCK_EXPERIENCES,
  MOCK_MULTIPLIER_EVENTS, MOCK_BUNDLES
} from '../../data/go2LegendeMock';
import { DashboardSummary } from '../../components/go2Legende/DashboardSummary';
import { TripsTable } from '../../components/go2Legende/TripsTable';
import { PointsHistory } from '../../components/go2Legende/PointsHistory';
import { AchievementsList } from '../../components/go2Legende/AchievementsList';
import { ReferAFriend } from '../../components/go2Legende/ReferAFriend';
import { DailyCheckIn } from '../../components/go2Legende/DailyCheckIn';
import { TravelPassport } from '../../components/go2Legende/TravelPassport';
import { SeasonalChallenges } from '../../components/go2Legende/SeasonalChallenges';
import { SpinTheWheel } from '../../components/go2Legende/SpinTheWheel';
import { Leaderboard } from '../../components/go2Legende/Leaderboard';
import { DestinationBingo } from '../../components/go2Legende/DestinationBingo';
import { ExperienceMarketplace } from '../../components/go2Legende/ExperienceMarketplace';
import { MultiplierEvents } from '../../components/go2Legende/MultiplierEvents';
import { BundleBuilder } from '../../components/go2Legende/BundleBuilder';
import { LayoutDashboard, Plane, Target, Sparkles, Trophy, Users, LogOut, Flame, Award, MapPin } from 'lucide-react';

type TabType = 'pregled' | 'putovanja' | 'izazovi' | 'iskustva' | 'rang-lista' | 'preporuci';
const VALID_TABS: TabType[] = ['pregled', 'putovanja', 'izazovi', 'iskustva', 'rang-lista', 'preporuci'];

export const DashboardPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabType | null;
  const [activeTab, setActiveTab] = useState<TabType>(
    tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'pregled'
  );

  useEffect(() => {
    if (tabParam && VALID_TABS.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Get active multiplier event
  const activeMultiplierEvent = MOCK_MULTIPLIER_EVENTS.find(event => event.active);

  // Get active challenges for preview
  const activeChallenges = MOCK_SEASONAL_CHALLENGES.filter(ch => !ch.completed).slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50 font-sans flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center mr-8">
                <img src="/assets/go2-logo.png" alt="Go2 Logo" className="h-8 w-auto" />
              </Link>
              <nav className="hidden md:flex space-x-1">
                <span className="text-gray-400">/</span>
                <Link to="/" className="text-gray-500 hover:text-go2-pink px-2">Go2 Legende</Link>
                <span className="text-gray-400">/</span>
                <span className="text-go2-pink font-medium px-2">Kontrolna tabla</span>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600 hidden sm:block">Dobrodošao/la, {MOCK_USER.name}</span>
              <div className="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center text-brand-600 font-bold">
                {MOCK_USER.name.charAt(0)}
              </div>
              <Link to="/" className="text-gray-400 hover:text-gray-600">
                <LogOut className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-grow w-full">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-go2-pink">Tvoja Go2 Legende kontrolna tabla</h1>
          <p className="text-gray-600">Prati svoje bodove, nivo i nagrade na jednom mestu.</p>
        </div>

        <DashboardSummary user={MOCK_USER} />

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8 mt-8">
          <nav className="-mb-px flex space-x-2 overflow-x-auto">
            <button
              onClick={() => handleTabChange('pregled')}
              className={`whitespace-nowrap py-2.5 px-4 rounded-t-lg font-medium text-sm flex items-center transition-colors ${
                activeTab === 'pregled'
                  ? 'bg-go2-pink text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Pregled
            </button>
            <button
              onClick={() => handleTabChange('putovanja')}
              className={`whitespace-nowrap py-2.5 px-4 rounded-t-lg font-medium text-sm flex items-center transition-colors ${
                activeTab === 'putovanja'
                  ? 'bg-go2-pink text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Plane className="w-4 h-4 mr-2" />
              Putovanja
            </button>
            <button
              onClick={() => handleTabChange('izazovi')}
              className={`whitespace-nowrap py-2.5 px-4 rounded-t-lg font-medium text-sm flex items-center transition-colors ${
                activeTab === 'izazovi'
                  ? 'bg-go2-pink text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Target className="w-4 h-4 mr-2" />
              Izazovi
            </button>
            <button
              onClick={() => handleTabChange('iskustva')}
              className={`whitespace-nowrap py-2.5 px-4 rounded-t-lg font-medium text-sm flex items-center transition-colors ${
                activeTab === 'iskustva'
                  ? 'bg-go2-pink text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Iskustva
            </button>
            <button
              onClick={() => handleTabChange('rang-lista')}
              className={`whitespace-nowrap py-2.5 px-4 rounded-t-lg font-medium text-sm flex items-center transition-colors ${
                activeTab === 'rang-lista'
                  ? 'bg-go2-pink text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Trophy className="w-4 h-4 mr-2" />
              Rang Lista
            </button>
            <button
              onClick={() => handleTabChange('preporuci')}
              className={`whitespace-nowrap py-2.5 px-4 rounded-t-lg font-medium text-sm flex items-center transition-colors ${
                activeTab === 'preporuci'
                  ? 'bg-go2-pink text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Users className="w-4 h-4 mr-2" />
              Preporuči
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          {activeTab === 'pregled' && (
            <div className="space-y-6">
              {/* Active Multiplier Event Banner */}
              {activeMultiplierEvent && (
                <div className="bg-gradient-to-r from-go2-pink to-purple-600 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center mb-2">
                        <span className="text-3xl mr-3">{activeMultiplierEvent.emoji}</span>
                        <h3 className="text-xl font-bold">{activeMultiplierEvent.title}</h3>
                      </div>
                      <p className="text-white/90 mb-2">{activeMultiplierEvent.description}</p>
                      <p className="text-sm text-white/80">
                        Važi do: {new Date(activeMultiplierEvent.endDate).toLocaleDateString('sr-RS')}
                      </p>
                    </div>
                    <div className="hidden md:flex items-center justify-center bg-white/20 rounded-full w-24 h-24">
                      <span className="text-4xl font-bold">{activeMultiplierEvent.multiplier}x</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                  <Flame className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-go2-pink">{MOCK_USER.check_in_streak}</div>
                  <div className="text-xs text-gray-500">Dana Streak</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                  <Trophy className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-go2-pink">
                    #{MOCK_LEADERBOARD.find(e => e.isCurrentUser)?.rank || 0}
                  </div>
                  <div className="text-xs text-gray-500">Rang</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                  <MapPin className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-go2-pink">{MOCK_TRAVEL_STAMPS.length}</div>
                  <div className="text-xs text-gray-500">Pečata</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                  <Award className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-go2-pink">
                    {MOCK_ACHIEVEMENTS.filter(a => a.is_unlocked).length}
                  </div>
                  <div className="text-xs text-gray-500">Dostignuća</div>
                </div>
              </div>

              {/* Daily Check-In Widget */}
              <DailyCheckIn
                user={MOCK_USER}
                checkInHistory={MOCK_CHECK_IN_HISTORY}
              />

              {/* Active Challenges Preview */}
              {activeChallenges.length > 0 && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-go2-pink">Aktivni Izazovi</h3>
                    <button
                      onClick={() => handleTabChange('izazovi')}
                      className="text-sm text-brand-600 font-medium hover:text-brand-800"
                    >
                      Vidi sve
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {activeChallenges.map((challenge) => (
                      <div key={challenge.id} className="bg-white rounded-xl p-4 border border-gray-200">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-bold text-go2-pink text-sm">{challenge.title}</h4>
                          <span className="text-yellow-500 font-bold text-xs">{challenge.reward} bodova</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                          <div
                            className="bg-go2-pink rounded-full h-2 transition-all"
                            style={{ width: `${(challenge.progress / challenge.target) * 100}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500">
                          {challenge.progress} / {challenge.target} · do {new Date(challenge.deadline).toLocaleDateString('sr-RS', { day: 'numeric', month: 'short' })}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-bold text-go2-pink mb-4">Nedavna Aktivnost</h3>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <ul className="divide-y divide-gray-100">
                      {MOCK_TRANSACTIONS.slice(0, 3).map((tx) => (
                        <li key={tx.id} className="p-4 hover:bg-gray-50">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-sm font-medium text-go2-pink">{tx.description}</p>
                              <p className="text-xs text-gray-500">{tx.date}</p>
                            </div>
                            <span className={`text-sm font-bold ${tx.points > 0 ? 'text-green-600' : 'text-red-500'}`}>
                              {tx.points > 0 ? '+' : ''}{tx.points}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                    <div className="p-4 bg-gray-50 border-t border-gray-100 text-center">
                      <button
                        onClick={() => handleTabChange('putovanja')}
                        className="text-sm text-brand-600 font-medium hover:text-brand-800"
                      >
                        Vidi sve aktivnosti
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-go2-pink mb-4">Brze Akcije</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <a
                      href="https://go2.rs"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-shadow text-left block"
                    >
                      <Plane className="w-6 h-6 text-brand-500 mb-2" />
                      <div className="font-bold text-go2-pink">Rezerviši putovanje</div>
                      <div className="text-xs text-gray-500">Pronađi sledeću avanturu</div>
                    </a>
                    <button
                      onClick={() => handleTabChange('iskustva')}
                      className="p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-shadow text-left"
                    >
                      <Sparkles className="w-6 h-6 text-purple-500 mb-2" />
                      <div className="font-bold text-go2-pink">Posebna iskustva</div>
                      <div className="text-xs text-gray-500">Unapređi putovanje</div>
                    </button>
                    <button
                      onClick={() => handleTabChange('rang-lista')}
                      className="p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-shadow text-left"
                    >
                      <Trophy className="w-6 h-6 text-yellow-500 mb-2" />
                      <div className="font-bold text-go2-pink">Točak Sreće</div>
                      <div className="text-xs text-gray-500">Osvoji bonus bodove</div>
                    </button>
                    <button
                      onClick={() => handleTabChange('preporuci')}
                      className="p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-shadow text-left"
                    >
                      <Users className="w-6 h-6 text-indigo-500 mb-2" />
                      <div className="font-bold text-go2-pink">Pozovi prijatelje</div>
                      <div className="text-xs text-gray-500">Zaradi 300 bodova po preporuci</div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'putovanja' && (
            <div className="space-y-8">
              <TripsTable bookings={MOCK_BOOKINGS} />
              <TravelPassport stamps={MOCK_TRAVEL_STAMPS} />
              <PointsHistory transactions={MOCK_TRANSACTIONS} />
            </div>
          )}

          {activeTab === 'izazovi' && (
            <div className="space-y-8">
              <SeasonalChallenges challenges={MOCK_SEASONAL_CHALLENGES} />
              <DestinationBingo cells={MOCK_BINGO} />
              <AchievementsList achievements={MOCK_ACHIEVEMENTS} />
            </div>
          )}

          {activeTab === 'iskustva' && (
            <div className="space-y-8">
              <MultiplierEvents events={MOCK_MULTIPLIER_EVENTS} />
              <ExperienceMarketplace experiences={MOCK_EXPERIENCES} />
              <BundleBuilder bundles={MOCK_BUNDLES} />
            </div>
          )}

          {activeTab === 'rang-lista' && (
            <div className="space-y-8">
              <Leaderboard entries={MOCK_LEADERBOARD} />
              <SpinTheWheel prizes={MOCK_SPIN_PRIZES} />
            </div>
          )}

          {activeTab === 'preporuci' && (
            <ReferAFriend user={MOCK_USER} />
          )}
        </div>
      </main>

      <footer className="bg-white border-t border-gray-200 py-8 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center">
          <div className="text-gray-500 text-sm mb-4 md:mb-0">
            &copy; {new Date().getFullYear()} Go2 Event d.o.o.
          </div>
          <div className="flex space-x-6 text-sm text-gray-500">
            <Link to="/pravila" className="hover:text-go2-pink">Pravila programa</Link>
            <Link to="/faq" className="hover:text-go2-pink">FAQ</Link>
            <a href="mailto:office@go2.rs" className="hover:text-go2-pink">Kontakt</a>
          </div>
        </div>
      </footer>
    </div>
  );
};
